import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from '@/components/ErrorBoundary';
import MobileGate from '@/components/MobileGate';
import { AuthProvider } from '@/context/AuthContext';
import { queryClient } from '@/lib/queryClient';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppShell from '@/components/layout/AppShell';
import LoginPage from '@/features/auth/LoginPage';
import ForgotPasswordPage from '@/features/auth/ForgotPasswordPage';
import PendingOrgPage from '@/features/auth/PendingOrgPage';
import ChatLayout from '@/features/chat/ChatLayout';
import PrayersLayout from '@/features/prayers/PrayersLayout';
import EventsPage from '@/features/events/EventsPage';
import EventDetailPage from '@/features/events/EventDetailPage';
import RosterLayout from '@/features/roster/RosterLayout';
import PollsLayout from '@/features/polls/PollsLayout';
import SignupsLayout from '@/features/signups/SignupsLayout';

function PagePlaceholder({ title }: { title: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <p className="text-flock-navy-400">{title} — coming soon</p>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    {/* Phones/tablets get a "download the app" interstitial instead of the app */}
    <MobileGate>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/pending"
              element={
                <ProtectedRoute>
                  <PendingOrgPage />
                </ProtectedRoute>
              }
            />
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route path="/chat" element={<ChatLayout />} />
              <Route path="/chat/:groupId" element={<ChatLayout />} />
              <Route path="/chat/:groupId/:chatGroupId" element={<ChatLayout />} />
              <Route path="/prayers" element={<PrayersLayout />} />
              <Route path="/prayers/:groupId" element={<PrayersLayout />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:eventId" element={<EventDetailPage />} />
              <Route path="/polls" element={<PollsLayout />} />
              <Route path="/polls/:groupId" element={<PollsLayout />} />
              <Route path="/polls/:groupId/p/:pollId" element={<PollsLayout />} />
              <Route path="/signups" element={<SignupsLayout />} />
              <Route path="/signups/:groupId" element={<SignupsLayout />} />
              <Route path="/signups/:groupId/s/:sheetId" element={<SignupsLayout />} />
              <Route path="/roster" element={<RosterLayout />} />
              <Route path="/roster/org/:orgId" element={<RosterLayout />} />
              <Route path="/roster/org/:orgId/member/:memberId" element={<RosterLayout />} />
              <Route path="/roster/group/:groupId" element={<RosterLayout />} />
              <Route path="/roster/group/:groupId/member/:memberId" element={<RosterLayout />} />
              <Route path="/settings" element={<PagePlaceholder title="Settings" />} />
            </Route>
            <Route path="/" element={<Navigate to="/chat" replace />} />
            <Route path="*" element={<Navigate to="/chat" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </MobileGate>
    </ErrorBoundary>
  );
}
